import { Box, Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlateUtensils } from "@fortawesome/pro-light-svg-icons"
import { faArrowUpRight } from "@fortawesome/pro-light-svg-icons"

import { WeekMenuProduct } from "../../../weekMenuTypes"
import { ProductDisabledCover } from "../../../../../../../shared/components/productAvailability/ProductDisabledCover"
import { CardImage } from "../../CardImage"
import { ProductCardInfo } from "./components/ProductCardInfo"

type StyleProps = {
    onlyView?: boolean
    extendDetails?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { onlyView, extendDetails }) => ({
    card: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: onlyView ? "default" : "pointer",
        boxShadow: "none",
        borderRadius: extendDetails ? ".75rem" : 0,
        border: extendDetails ? `1px solid ${theme.palette.tailwind.gray[200]}` : "none",
        position: "relative",
        overflow: "hidden",
    },
    topContent: {
        position: "relative",
    },
    chipWrap: {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 2,
    },
    chip: {
        display: "flex",
        borderRadius: ".25rem",
        backgroundColor: theme.palette.tailwind.green[500],
        borderColor: theme.palette.tailwind.green[500],
        color: theme.palette.common.white,
        fontWeight: 500,
    },
    content: {
        padding: extendDetails ? "1rem" : ".375rem 0 0 0",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        "&:last-child": {
            paddingBottom: extendDetails ? "1rem" : 0,
        },
    },
    title: {
        fontSize: ".875rem",
        fontWeight: 500,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
    },
}))

type ProductCardProps = {
    resource: WeekMenuProduct
    onCardClick?: () => void
    extendDetails?: boolean
    onlyView?: boolean
}

export function ProductCard({ resource, onlyView, extendDetails, onCardClick }: ProductCardProps) {
    const { classes } = useStyles({ onlyView, extendDetails })

    return (
        <Card className={classes.card} onClick={onCardClick} elevation={0}>
            <ProductDisabledCover availability={resource.orderDetails?.availability} />
            <Box className={classes.topContent}>
                <CardImage imageUri={resource.imageUrl} alt={resource.subject} icon={faPlateUtensils} />
                {resource.headerTag && (
                    <Box className={classes.chipWrap}>
                        <Chip label={resource.headerTag} size="small" className={classes.chip} />
                    </Box>
                )}
            </Box>

            <CardContent className={classes.content}>
                {extendDetails ? (
                    <ProductCardInfo resource={resource} />
                ) : (
                    <Stack direction="row" alignItems="start" justifyContent="space-between" gap=".5rem">
                        <Tooltip title={resource.subject} arrow>
                            <Typography className={classes.title} component="div">
                                {resource.subject}
                            </Typography>
                        </Tooltip>
                        <IconButton onClick={onCardClick} size="small">
                            <FontAwesomeIcon size="xs" icon={faArrowUpRight} />
                        </IconButton>
                    </Stack>
                )}
            </CardContent>
        </Card>
    )
}
